import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { AppShell, Badge, Burger, Group, Skeleton, Title } from '@mantine/core';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAdjustments,
  IconAddressBook,
  IconApps,
  IconBolt,
  IconBrandFacebook,
  IconBrandGoogle,
  IconBrandInstagram,
  IconBrandMeta,
  IconBrandWhatsapp,
  IconBuildingStore,
  IconChartLine,
  IconCode,
  IconCreditCard,
  IconDashboard,
  IconForms,
  IconLogout,
  IconMessage,
  IconMessageReply,
  IconPhoto,
  IconPlugConnected,
  IconRobot,
  IconRocket,
  IconRoute,
  IconSend,
  IconSitemap,
  IconSparkles,
  IconSpeakerphone,
  IconStethoscope,
  IconTemplate,
  IconToggleRight,
  IconUsers,
  IconShoppingCart,
  IconReceipt,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';
import { ImpersonationPill } from '../../components/ImpersonationPill';
import { GroupedNav, type NavGroup } from '../../components/GroupedNav';
import { LightboxProvider } from '../../components/inbox/Lightbox';
import { UserMenu } from '../../components/UserMenu';
import { useInboxNavRealtime } from '../../lib/realtime';
import { usePermissions, type PermissionKey } from '../../lib/permissions';

const VENDOR_GROUPS: NavGroup[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    leftSection: <IconDashboard size={16} />,
    // Dashboard is a single-link group; keeping it as a group entry
    // means the parent click becomes a redundant disclosure. Trick: a
    // single child that shares the parent label.
    children: [{ label: 'Overview', to: '/vendor', matchPrefix: false }],
  },
  {
    id: 'inboxes',
    label: 'Inboxes',
    leftSection: <IconMessage size={16} />,
    children: [
      {
        label: 'WhatsApp chat',
        to: '/vendor/chat',
        matchPrefix: true,
        leftSection: <IconBrandWhatsapp size={14} />,
        requires: 'messaging',
      },
      {
        label: 'Messenger inbox',
        to: '/vendor/facebook/inbox',
        matchPrefix: true,
        leftSection: <IconBrandFacebook size={14} />,
        requires: 'messaging',
      },
      {
        label: 'Instagram inbox',
        to: '/vendor/instagram/inbox',
        matchPrefix: true,
        leftSection: <IconBrandInstagram size={14} />,
        requires: 'messaging',
      },
      {
        label: 'Media files',
        to: '/vendor/media',
        matchPrefix: true,
        leftSection: <IconPhoto size={14} />,
        requires: 'messaging',
      },
      {
        label: 'Google reviews',
        to: '/vendor/google/reviews',
        matchPrefix: true,
        leftSection: <IconBrandGoogle size={14} />,
        requires: 'messaging',
      },
    ],
  },
  {
    id: 'audience',
    label: 'Contacts',
    leftSection: <IconAddressBook size={16} />,
    // Single child → GroupedNav auto-flattens to a clean "Contacts" leaf.
    // Groups / Labels / Custom fields are now tabs inside this page; Auto-labels
    // is reached from the Labels tab; CRM connections moved to Connections.
    // matchPrefix keeps it lit on the contact detail + auto-label sub-routes.
    children: [
      {
        label: 'Contacts',
        to: '/vendor/contacts',
        matchPrefix: true,
        requires: 'manage_contacts',
      },
    ],
  },
  {
    id: 'outreach',
    label: 'Outreach',
    leftSection: <IconSpeakerphone size={16} />,
    children: [
      {
        label: 'Campaigns',
        to: '/vendor/campaigns',
        matchPrefix: true,
        leftSection: <IconSend size={14} />,
        requires: 'manage_campaigns',
      },
      {
        // WhatsApp / Instagram / Messenger templates merged into one tabbed page.
        label: 'Templates',
        to: '/vendor/templates',
        matchPrefix: true,
        leftSection: <IconTemplate size={14} />,
        requires: 'manage_templates',
      },
      {
        label: 'Forms',
        to: '/vendor/forms',
        matchPrefix: true,
        leftSection: <IconForms size={14} />,
        requires: 'messaging',
      },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    leftSection: <IconShoppingCart size={16} />,
    children: [
      {
        label: 'Orders',
        to: '/vendor/orders',
        matchPrefix: true,
        leftSection: <IconReceipt size={14} />,
        requires: 'messaging',
      },
      {
        label: 'Product catalog',
        to: '/vendor/catalog',
        matchPrefix: true,
        leftSection: <IconBuildingStore size={14} />,
        requires: 'manage_integrations',
      },
    ],
  },
  {
    id: 'automation',
    label: 'Automation',
    leftSection: <IconRobot size={16} />,
    children: [
      {
        label: 'Auto-replies',
        to: '/vendor/bots/replies',
        leftSection: <IconMessageReply size={14} />,
        requires: 'manage_bots',
      },
      {
        label: 'Chat flows',
        to: '/vendor/bots/flows',
        matchPrefix: true,
        leftSection: <IconSitemap size={14} />,
        requires: 'manage_bots',
      },
      {
        label: 'AI Agents',
        to: '/vendor/settings/ai-agents',
        matchPrefix: true,
        leftSection: <IconSparkles size={14} />,
        requires: 'manage_bots',
      },
    ],
  },
  {
    id: 'connections',
    label: 'Connections',
    leftSection: <IconPlugConnected size={16} />,
    children: [
      {
        label: 'Meta setup',
        to: '/vendor/integrations/meta',
        matchPrefix: true,
        leftSection: <IconBrandMeta size={14} />,
        requires: 'manage_integrations',
      },
      {
        label: 'Google setup',
        to: '/vendor/integrations/google',
        matchPrefix: true,
        leftSection: <IconBrandGoogle size={14} />,
        requires: 'manage_integrations',
      },
      {
        label: 'E-commerce',
        to: '/vendor/integrations/ecommerce',
        matchPrefix: true,
        leftSection: <IconBuildingStore size={14} />,
        requires: 'manage_integrations',
      },
      {
        // Moved out of the Audience section — a CRM is an external integration.
        label: 'CRM connections',
        to: '/vendor/contacts/crm',
        matchPrefix: true,
        leftSection: <IconAddressBook size={14} />,
        requires: 'manage_contacts',
      },
      {
        label: 'API & keys',
        to: '/vendor/integrations/api',
        matchPrefix: true,
        leftSection: <IconCode size={14} />,
        requires: 'manage_integrations',
      },
      {
        label: 'Connected apps',
        to: '/vendor/integrations/connected-apps',
        matchPrefix: true,
        leftSection: <IconApps size={14} />,
        requires: 'manage_integrations',
      },
      {
        label: 'Page insights',
        to: '/vendor/pages',
        matchPrefix: true,
        leftSection: <IconChartLine size={14} />,
        requires: 'manage_integrations',
      },
      {
        label: 'Connection test',
        to: '/vendor/connection-test',
        matchPrefix: true,
        leftSection: <IconStethoscope size={14} />,
        requires: 'manage_integrations',
      },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    leftSection: <IconAdjustments size={16} />,
    children: [
      {
        label: 'Team members',
        to: '/vendor/team',
        leftSection: <IconUsers size={14} />,
        requires: 'manage_team',
      },
      {
        label: 'Subscription',
        to: '/vendor/subscription',
        leftSection: <IconCreditCard size={14} />,
        requires: 'view_billing',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    leftSection: <IconBolt size={16} />,
    children: [
      {
        label: 'General',
        to: '/vendor/settings/general',
        leftSection: <IconAdjustments size={14} />,
        adminOnly: true,
      },
      {
        label: 'AI & timing',
        to: '/vendor/settings/ai-and-timing',
        leftSection: <IconRobot size={14} />,
        requires: 'manage_bots',
      },
      {
        label: 'Team assignment',
        to: '/vendor/settings/routing',
        leftSection: <IconRoute size={14} />,
        adminOnly: true,
      },
      {
        label: 'Features',
        to: '/vendor/settings/features',
        leftSection: <IconToggleRight size={14} />,
        requires: 'manage_integrations',
      },
    ],
  },
];

/** Small red unread pill for the nav badges; caps at 99+. */
function UnreadBadge({ count }: { count: number }) {
  return (
    <Badge size="sm" variant="filled" color="red" circle={count < 10}>
      {count > 99 ? '99+' : count}
    </Badge>
  );
}

function VendorShell() {
  const [opened, { toggle }] = useDisclosure();
  const me = trpc.auth.me.useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const { can, isVendorAdmin, isSuperAdmin } = usePermissions();

  useEffect(() => {
    if (me.isFetched && (!me.data || me.data.roles.includes('SuperAdmin'))) {
      if (!me.data) navigate({ to: '/auth/login' });
      else if (me.data.roles.includes('SuperAdmin')) navigate({ to: '/central' });
    }
  }, [me.isFetched, me.data, navigate]);

  // Filter the static nav definition by the current user's permissions.
  // Empty groups (every child filtered out) drop entirely so a section
  // header doesn't sit alone with no children. VendorAdmin / SuperAdmin
  // bypass everything via `can(...)`'s built-in admin shortcut.
  const visibleGroups = useMemo<NavGroup[]>(() => {
    return VENDOR_GROUPS.map((g) => ({
      ...g,
      children: g.children.filter((c) => {
        if (c.adminOnly) return isVendorAdmin || isSuperAdmin;
        if (!c.requires) return true;
        return can(c.requires as PermissionKey);
      }),
    })).filter((g) => g.children.length > 0);
  }, [can, isVendorAdmin, isSuperAdmin]);

  // Per-inbox unread totals for the nav "Inboxes" badges. Gated on the
  // messaging permission (the inbox children require it; without it the group
  // is filtered out above). Kept live from any page by useInboxNavRealtime.
  const canMessaging = can('messaging' as PermissionKey);
  const waUnread = trpc.chat.totalUnread.useQuery(undefined, { enabled: canMessaging });
  const fbUnread = trpc.facebook.inbox.totalUnread.useQuery(undefined, { enabled: canMessaging });
  const igUnread = trpc.instagram.inbox.totalUnread.useQuery(undefined, { enabled: canMessaging });
  useInboxNavRealtime();

  // Guided setup is a platform rollout flag. While it is off the nav is exactly
  // what it is today; while it is on, an unfinished vendor gets a way back into
  // the corridor that doesn't depend on remembering the URL.
  const canIntegrations = can('manage_integrations' as PermissionKey);
  const onboardingMethods = trpc.integrations.meta.onboardingMethods.useQuery(undefined, {
    enabled: canIntegrations,
  });
  const setupState = trpc.integrations.meta.onboarding.getState.useQuery(undefined, {
    enabled: canIntegrations,
  });
  const onboardingV2 = onboardingMethods.data?.onboardingV2Enabled ?? false;
  const showSetupEntry = onboardingV2 && !setupState.data?.completedAt;

  const navGroups = useMemo<NavGroup[]>(() => {
    const counts: Record<string, number> = {
      '/vendor/chat': waUnread.data ?? 0,
      '/vendor/facebook/inbox': fbUnread.data ?? 0,
      '/vendor/instagram/inbox': igUnread.data ?? 0,
    };
    return visibleGroups.map((g) => {
      if (g.id === 'connections' && onboardingV2) {
        // v2 on: one "Connections" destination replaces the Meta-setup entry,
        // and the classic page stays reachable from inside it (and by URL)
        // until cutover. v2 off: this whole branch is skipped and the nav is
        // byte-for-byte what it is today.
        return {
          ...g,
          children: [
            ...(showSetupEntry
              ? [
                  {
                    label: 'Finish setup',
                    to: '/vendor/setup',
                    matchPrefix: true,
                    leftSection: <IconRocket size={14} />,
                    requires: 'manage_integrations',
                  },
                ]
              : []),
            {
              label: 'Connections',
              to: '/vendor/connections',
              matchPrefix: true,
              leftSection: <IconPlugConnected size={14} />,
              requires: 'manage_integrations',
            },
            // The Meta setup page and the standalone connection test both fold
            // into the hub; drop them from the nav so there is one door.
            ...g.children.filter(
              (c) => c.to !== '/vendor/integrations/meta' && c.to !== '/vendor/connection-test',
            ),
          ],
        };
      }
      if (g.id !== 'inboxes') return g;
      let total = 0;
      const children = g.children.map((c) => {
        const n = counts[c.to] ?? 0;
        total += n;
        return n > 0 ? { ...c, rightSection: <UnreadBadge count={n} /> } : c;
      });
      return { ...g, children, badge: total > 0 ? <UnreadBadge count={total} /> : undefined };
    });
  }, [visibleGroups, waUnread.data, fbUnread.data, igUnread.data, showSetupEntry, onboardingV2]);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={4}>LeadSync</Title>
          </Group>
          <Group gap="sm">
            {me.data?.isImpersonating && <ImpersonationPill />}
            <UserMenu />
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p={0}>
        <GroupedNav
          scope="vendor"
          groups={navGroups}
          trailer={{
            label: 'Sign out',
            to: '/auth/logout',
            leftSection: <IconLogout size={16} />,
          }}
        />
      </AppShell.Navbar>
      <AppShell.Main>
        {me.isPending ? (
          <Skeleton height={400} />
        ) : (
          // Reset the boundary on route changes so a fixed error doesn't
          // stick after the user navigates away.
          <ErrorBoundary resetKey={location.pathname}>
            <LightboxProvider>
              <Outlet />
            </LightboxProvider>
          </ErrorBoundary>
        )}
      </AppShell.Main>
    </AppShell>
  );
}

export const Route = createFileRoute('/vendor')({ component: VendorShell });
